import { logger } from './logger';

/**
 * Cache entry interface
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

/**
 * Cache options for individual requests
 */
export interface CacheOptions {
  /** Time to live in milliseconds */
  ttl?: number;
  /** Cache key override (auto-generated if not provided) */
  key?: string;
  /** Whether to use cache for this request */
  enabled?: boolean;
  /** Whether to force refresh the cache */
  forceRefresh?: boolean;
  /** Custom cache tags for invalidation */
  tags?: string[];
}

/**
 * Cache configuration
 */
export interface CacheConfig {
  /** Default TTL for all cached entries (in milliseconds) */
  defaultTTL: number;
  /** Maximum number of entries to store */
  maxSize: number;
  /** Enable/disable caching globally */
  enabled: boolean;
}

// Guard to prevent duplicate process signal listeners across HMR re-evaluations
let cacheProcessListenersRegistered = false;

/**
 * In-memory cache implementation with TTL support
 */
export class Cache {
  private static instance: Cache;
  private cache: Map<string, CacheEntry<any>>;
  private config: CacheConfig;
  private tagMap: Map<string, Set<string>>; // tag -> Set of cache keys
  private cleanupInterval: NodeJS.Timeout | null = null;

  private constructor(config?: Partial<CacheConfig>) {
    this.cache = new Map();
    this.tagMap = new Map();
    this.config = {
      defaultTTL: 60 * 1000, // 1 minute default (reduced for memory)
      maxSize: 100, // Reduced from 1000 to prevent memory buildup
      enabled: true,
      ...config,
    };

    // Periodic cleanup of expired entries
    this.startCleanupInterval();
  }

  /**
   * Get singleton instance
   */
  static getInstance(config?: Partial<CacheConfig>): Cache {
    if (!Cache.instance) {
      Cache.instance = new Cache(config);
    }
    return Cache.instance;
  }

  /**
   * Generate cache key from URL and params
   */
  generateKey(url: string, params?: Record<string, any>): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${url}:${paramString}`;
  }

  /**
   * Get cached data
   */
  get<T>(key: string): T | null {
    if (!this.config.enabled) {
      return null;
    }

    const entry = this.cache.get(key);
    if (!entry) {
      logger.debug({ msg: 'Cache miss', key });
      return null;
    }

    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if entry has expired
    if (age > entry.ttl) {
      logger.debug({ msg: 'Cache expired', key, age, ttl: entry.ttl });
      this.delete(key);
      return null;
    }

    logger.debug({ msg: 'Cache hit', key, age, ttl: entry.ttl });
    return entry.data as T;
  }

  /**
   * Peek at a cache entry including metadata.
   */
  peek<T>(key: string): CacheEntry<T> | null {
    if (!this.config.enabled) return null;
    const entry = this.cache.get(key);
    return entry ?? null;
  }

  /**
   * Set cached data
   */
  set<T>(key: string, data: T, ttl?: number, tags?: string[]): void {
    if (!this.config.enabled) {
      return;
    }

    // Enforce max size by removing oldest entries (more aggressive)
    while (this.cache.size >= this.config.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.delete(firstKey);
      } else {
        break;
      }
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl ?? this.config.defaultTTL,
    };

    this.cache.set(key, entry);

    // Store tags
    if (tags && tags.length > 0) {
      tags.forEach((tag) => {
        if (!this.tagMap.has(tag)) {
          this.tagMap.set(tag, new Set());
        }
        this.tagMap.get(tag)!.add(key);
      });
    }

    logger.debug({ msg: 'Cache set', key, ttl: entry.ttl, tags });
  }

  /**
   * Delete a cache entry
   */
  delete(key: string): void {
    // Remove from tag map
    this.tagMap.forEach((keys) => {
      keys.delete(key);
    });

    this.cache.delete(key);
    logger.debug({ msg: 'Cache deleted', key });
  }

  /**
   * Clear entire cache
   */
  clear(): void {
    this.cache.clear();
    this.tagMap.clear();
    logger.debug({ msg: 'Cache cleared' });
  }

  /**
   * Invalidate cache by tags
   */
  invalidateByTag(tag: string): void {
    const keys = this.tagMap.get(tag);
    if (keys) {
      keys.forEach((key) => {
        this.cache.delete(key);
      });
      this.tagMap.delete(tag);
      logger.debug({ msg: 'Cache invalidated by tag', tag, count: keys.size });
    }
  }

  /**
   * Invalidate cache by pattern
   */
  invalidateByPattern(pattern: RegExp): void {
    let count = 0;
    for (const key of this.cache.keys()) {
      if (pattern.test(key)) {
        this.delete(key);
        count++;
      }
    }
    logger.debug({ msg: 'Cache invalidated by pattern', pattern: pattern.toString(), count });
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    maxSize: number;
    enabled: boolean;
    defaultTTL: number;
    tags: number;
  } {
    return {
      size: this.cache.size,
      maxSize: this.config.maxSize,
      enabled: this.config.enabled,
      defaultTTL: this.config.defaultTTL,
      tags: this.tagMap.size,
    };
  }

  /**
   * Update cache configuration
   */
  updateConfig(config: Partial<CacheConfig>): void {
    this.config = { ...this.config, ...config };
    logger.debug({ msg: 'Cache config updated', config: this.config });
  }

  /**
   * Start periodic cleanup of expired entries
   */
  private startCleanupInterval(): void {
    // Clear existing interval if any
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    // Do not start background cleanup when running Jest, as it creates open handles.
    if (process.env.JEST_WORKER_ID) {
      logger.debug({ msg: 'Cache cleanup interval disabled during tests' });
      return;
    }

    // Run cleanup every 30 seconds (more frequent to prevent buildup)
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 30 * 1000);

    // Cleanup on process exit (Node.js only) — register once per process lifetime
    if (typeof process !== 'undefined' && !cacheProcessListenersRegistered) {
      cacheProcessListenersRegistered = true;
      process.on('exit', () => this.destroy());
      process.on('SIGINT', () => {
        this.destroy();
        process.exit(0);
      });
      process.on('SIGTERM', () => {
        this.destroy();
        process.exit(0);
      });
    }
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    let cleaned = 0;
    const keysToDelete: string[] = [];

    // Collect expired keys first to avoid modification during iteration
    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age > entry.ttl) {
        keysToDelete.push(key);
      }
    }

    // Delete expired keys
    keysToDelete.forEach((key) => {
      this.delete(key);
      cleaned++;
    });

    // If cache is still too large, remove oldest entries
    if (this.cache.size > this.config.maxSize) {
      const excess = this.cache.size - this.config.maxSize;
      const oldestKeys = Array.from(this.cache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)
        .slice(0, excess)
        .map(([key]) => key);

      oldestKeys.forEach((key) => {
        this.delete(key);
        cleaned++;
      });
    }

    if (cleaned > 0) {
      logger.debug({ msg: 'Cache cleanup completed', cleaned, currentSize: this.cache.size });
    }
  }

  /**
   * Destroy the cache instance and clean up resources
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.clear();
    logger.debug({ msg: 'Cache destroyed' });
  }
}

// Export singleton instance
export const cache = Cache.getInstance();

/**
 * Initialize cache with configuration from app.config.json
 */
export function initializeCacheWithConfig(config?: { enabled?: boolean; defaultTTL?: number; maxSize?: number }): void {
  if (config) {
    cache.updateConfig(config);
    logger.debug({ msg: 'Cache initialized with app config', config });
  }
}

/**
 * Get memory usage stats (Node.js only)
 */
export function getCacheMemoryStats() {
  if (typeof process !== 'undefined' && process.memoryUsage) {
    const usage = process.memoryUsage();
    return {
      heapUsed: Math.round(usage.heapUsed / 1024 / 1024),
      heapTotal: Math.round(usage.heapTotal / 1024 / 1024),
      external: Math.round(usage.external / 1024 / 1024),
      rss: Math.round(usage.rss / 1024 / 1024),
    };
  }
  return null;
}
